'use client';

import { useEffect } from 'react';
import toast, { Toaster, ToastBar, useToasterStore } from 'react-hot-toast';

const DEFAULT_DURATION = 5000;

/**
 * Grace period on top of a toast's own duration before the watchdog steps in.
 *
 * The library should always win the race and dismiss the toast itself; this only
 * fires when its timer has been paused and never resumed.
 */
const WATCHDOG_GRACE_MS = 1500;

export default function ClientToaster() {
    const { toasts } = useToasterStore();

    // ─── Watchdog ─────────────────────────────────────────────────────────────
    // react-hot-toast pauses the dismiss timer for EVERY toast while the pointer
    // is inside the toast container — `onMouseEnter: startPause` on its wrapper,
    // with the paused flag held once in the store rather than per toast.
    //
    // On iOS a tap fires that mouseenter, but the matching mouseleave frequently
    // never arrives: touch has no real hover, and the synthetic one sticks until
    // the user taps some other hoverable element. The pause is then permanent,
    // and because it is global it strands not just the toast that was tapped but
    // every toast raised afterwards for the life of the page. Reported from an
    // iPhone 2026-08-13: an "Item Added" and a "Download failed" toast both sat
    // on screen indefinitely.
    //
    // The library exposes no way to clear that pause, so this runs a timer of
    // its own that nothing can pause. Deadlines are computed from each toast's
    // own createdAt, so this effect re-running (it re-runs on every store
    // change) reschedules to the same instant rather than extending the life of
    // a toast that is already on screen.
    useEffect(() => {
        const now = Date.now();
        const timers = toasts
            .filter(t => t.visible)
            .map(t => {
                // A toast asking to stay forever is honoured — Infinity is a
                // deliberate choice at the call site, not a stuck timer.
                if (t.duration === Infinity) return null;

                // `t.duration` is NOT the duration the library actually times
                // against. A typed toast carries its type default (2s for
                // success, 4s for error) while the scheduler uses the
                // toaster-level default below, so toasts really live 5s. Taking
                // the larger of the two keeps this strictly a backstop; reading
                // t.duration alone would cut every success toast short by 1.5s.
                const duration = Math.max(Number(t.duration) || 0, DEFAULT_DURATION);
                const deadline = t.createdAt + duration + WATCHDOG_GRACE_MS;
                return setTimeout(() => toast.dismiss(t.id), Math.max(0, deadline - now));
            })
            .filter(Boolean);
        return () => timers.forEach(clearTimeout);
    }, [toasts]);

    return (
        <Toaster
            position="top-center"
            containerStyle={{
                // Push toasts below the fixed header on all screen sizes.
                // Uses the same CSS variable the header and main content use,
                // so they stay in sync if --header-height ever changes.
                top: 'calc(var(--header-height) + 8px)',
            }}
            toastOptions={{
                duration: DEFAULT_DURATION,
                style: {
                    maxWidth: '90vw',
                },
            }}
        >
            {/* Tap anywhere on a toast to dismiss it. Reaching for the message
                is the obvious thing to do on a phone when one is in the way, and
                before this it did nothing — worse, it was the very gesture that
                got the timer stuck. `pointerEvents` has to be set here because
                the container above it is `pointer-events: none`. */}
            {t => (
                <div
                    onClick={() => toast.dismiss(t.id)}
                    style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                >
                    <ToastBar toast={t} />
                </div>
            )}
        </Toaster>
    );
}
